import { ChevronDown } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { SAME_CLIENT_MULTI_SHIFT_OUTREACH_MAX } from '@apps/web/components/outreaches/edit_shifts_section';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Label } from '@apps/web/components/ui/label';
import { SearchInput } from '@apps/web/components/ui/search_input';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { type useCoverageShiftAvailability } from '@apps/web/features/coverage/api/shift_availability';
import { AVAILABILITY_MAP_STYLES } from '@apps/web/features/coverage/model/map_colors';
import {
    isUpcomingOpenShift,
    useCoverageOpenShiftWindow,
} from '@apps/web/features/coverage/model/open_shifts';
import { useOutreachCaregivers } from '@apps/web/features/coverage/model/use_outreach_caregivers';
import { MapManualOutreachModal } from '@apps/web/features/coverage/ui/map_manual_outreach_modal';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import {
    formatDateAndTimeWithWeekday,
    formatTime,
} from '@apps/web/lib/date_utils';
import { useMode } from '@apps/web/lib/mode_store';
import {
    useCalendarShiftsInRange,
    useOrganization,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type CalendarShiftListResponse,
    type WorkforceMapPoint,
} from '@libraries/typescript/api_client';

const PAGE_SIZE = 25;
const STATUS_LABELS = {
    available: 'Available',
    unavailable: 'Unavailable',
    scheduled: 'Already scheduled',
    unknown: 'Availability unknown',
};

/** Owns shift-specific selection; remount for another client or organization. */
export function ClientOutreachPanel({
    organizationId,
    clientId,
    caregivers,
    selectedShifts,
    onSelectedShiftsChange,
    onSelectedCaregiverIdsChange,
    availability,
}: {
    readonly selectedShifts: CalendarShiftListResponse['shifts'];
    readonly onSelectedShiftsChange: (
        shifts: CalendarShiftListResponse['shifts'],
    ) => void;
    readonly onSelectedCaregiverIdsChange?: (
        ids: ReadonlyArray<string>,
    ) => void;
    readonly availability: Pick<
        ReturnType<typeof useCoverageShiftAvailability>,
        'statuses' | 'isLoading' | 'isValidating' | 'error' | 'mutate'
    >;
    readonly organizationId: string;
    readonly clientId: string;
    readonly caregivers: ReadonlyArray<{
        readonly point: WorkforceMapPoint;
        readonly miles: number;
    }>;
}) {
    const { formatName } = useFormatPersonName();
    const { timezone } = useOrganizationTimezone();
    const mode = useMode() ?? 'live';
    const { data: organization } = useOrganization(organizationId, {
        reason: 'coverage-manual-outreach',
    });
    const { rangeStart, rangeEnd, refresh } = useCoverageOpenShiftWindow();
    const shifts = useCalendarShiftsInRange(
        organizationId,
        { reason: 'coverage-client-open-shifts' },
        {
            clientId,
            shiftStartDate: rangeStart,
            shiftEndDate: rangeEnd,
        },
        { revalidateOnFocus: false },
    );
    const openShifts = useMemo(
        () =>
            shifts.shifts
                .filter((shift) =>
                    isUpcomingOpenShift(shift, new Date(rangeStart).getTime()),
                )
                .sort(
                    (a, b) =>
                        a.shift_start_time.localeCompare(b.shift_start_time) ||
                        a.id.localeCompare(b.id),
                ),
        [shifts.shifts, rangeStart],
    );
    const activeShiftIds = selectedShifts.map((shift) => shift.id);
    const shiftOptions = [
        ...selectedShifts,
        ...openShifts.filter((shift) => !activeShiftIds.includes(shift.id)),
    ].sort(
        (a, b) =>
            a.shift_start_time.localeCompare(b.shift_start_time) ||
            a.id.localeCompare(b.id),
    );
    const [profileCaregiverId, setProfileCaregiverId] = useState<string | null>(
        null,
    );
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const recipients = useOutreachCaregivers({
        organizationId,
        caregivers,
        shiftIds: activeShiftIds,
        availability,
    });
    const selectedCaregiverIds = recipients.selectedIds;
    useEffect(() => {
        onSelectedCaregiverIdsChange?.(selectedCaregiverIds);
    }, [onSelectedCaregiverIdsChange, selectedCaregiverIds]);
    useEffect(
        () => () => onSelectedCaregiverIdsChange?.([]),
        [onSelectedCaregiverIdsChange],
    );
    const visibleCaregivers = recipients.searching
        ? recipients.rows
        : recipients.rows.slice(0, visibleCount);
    const [composer, setComposer] = useState<{
        readonly shiftIds: Array<string>;
        readonly caregiverIds: Array<string>;
    } | null>(null);
    const manualAllowed =
        organization?.callout_settings?.manual_outreach_enabled === true;
    const sendingAllowed =
        mode === 'sandbox' ||
        organization?.features?.['outreach_sending_enabled'] === true;

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3 px-3 py-3">
            <div className="shrink-0 space-y-1.5">
                <Label htmlFor="coverage-open-shift">
                    Upcoming open shifts
                </Label>
                {shifts.error ? (
                    <div role="alert" className="text-destructive text-xs">
                        Couldn’t load open shifts.{' '}
                        <Button variant="link" size="sm" onClick={refresh}>
                            Try again
                        </Button>
                    </div>
                ) : !shifts.isLoading &&
                  !shifts.hasPendingPages &&
                  shiftOptions.length === 0 ? (
                    <p className="text-muted-foreground text-xs" role="status">
                        None
                    </p>
                ) : (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild={true}>
                            <Button
                                id="coverage-open-shift"
                                variant="outline"
                                className="w-full justify-between text-xs"
                                disabled={
                                    shifts.isLoading ||
                                    shifts.hasPendingPages ||
                                    shiftOptions.length === 0
                                }
                            >
                                {activeShiftIds.length > 0
                                    ? `${activeShiftIds.length} of ${SAME_CLIENT_MULTI_SHIFT_OUTREACH_MAX} shifts selected`
                                    : shifts.isLoading || shifts.hasPendingPages
                                      ? 'Loading shifts…'
                                      : 'Choose open shifts'}
                                <ChevronDown className="size-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="max-h-64 w-[var(--radix-dropdown-menu-trigger-width)]"
                            align="start"
                        >
                            {shiftOptions.map((shift) => (
                                <DropdownMenuCheckboxItem
                                    key={shift.id}
                                    checked={activeShiftIds.includes(shift.id)}
                                    disabled={
                                        !activeShiftIds.includes(shift.id) &&
                                        activeShiftIds.length >=
                                            SAME_CLIENT_MULTI_SHIFT_OUTREACH_MAX
                                    }
                                    onSelect={(event) => event.preventDefault()}
                                    onCheckedChange={(checked) =>
                                        onSelectedShiftsChange(
                                            checked
                                                ? [...selectedShifts, shift]
                                                : selectedShifts.filter(
                                                      (selected) =>
                                                          selected.id !==
                                                          shift.id,
                                                  ),
                                        )
                                    }
                                >
                                    {formatDateAndTimeWithWeekday(
                                        shift.shift_start_time,
                                        timezone,
                                    )}
                                    {' – '}
                                    {formatTime(shift.shift_end_time, timezone)}
                                </DropdownMenuCheckboxItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2">
                {activeShiftIds.length > 0 ? (
                    <SearchInput
                        aria-label="Search all caregivers"
                        placeholder="Search all caregivers"
                        value={recipients.search}
                        onValueChange={(value) => {
                            recipients.setSearch(value);
                            setVisibleCount(PAGE_SIZE);
                        }}
                        className="text-xs"
                    />
                ) : null}
                {activeShiftIds.length > 0 &&
                (availability.error || recipients.externalError) ? (
                    <div role="alert" className="text-destructive text-xs">
                        Couldn’t check availability.{' '}
                        <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                                if (availability.error) {
                                    void availability.mutate();
                                }
                                if (recipients.externalError) {
                                    recipients.retryExternalAvailability();
                                }
                            }}
                        >
                            Retry availability
                        </Button>
                    </div>
                ) : null}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {visibleCaregivers.map((caregiver) => {
                        const {
                            id,
                            name,
                            miles,
                            status,
                            loading,
                            error,
                            eligible,
                            selected,
                            currentStatus,
                        } = caregiver;
                        return (
                            <div
                                key={id}
                                className="flex items-center gap-2 py-2"
                            >
                                {activeShiftIds.length > 0 ? (
                                    <Checkbox
                                        aria-label={`Select ${formatName(name)}`}
                                        checked={selected}
                                        disabled={
                                            loading || !!error || !eligible
                                        }
                                        onCheckedChange={(checked) =>
                                            recipients.setSelected(
                                                caregiver,
                                                checked === true,
                                            )
                                        }
                                    />
                                ) : null}
                                <div className="min-w-0 grow text-xs">
                                    <button
                                        type="button"
                                        className="block max-w-full cursor-pointer truncate text-left underline underline-offset-2"
                                        onClick={() =>
                                            setProfileCaregiverId(id)
                                        }
                                    >
                                        {formatName(name)}
                                    </button>
                                    <span className="text-muted-foreground">
                                        {activeShiftIds.length === 0
                                            ? AVAILABILITY_MAP_STYLES[
                                                  currentStatus
                                              ].label
                                            : error
                                              ? 'Availability not checked'
                                              : loading
                                                ? 'Checking availability…'
                                                : status
                                                  ? status === 'available'
                                                      ? activeShiftIds.length ===
                                                        1
                                                          ? 'Available for this shift'
                                                          : 'Available for all selected shifts'
                                                      : STATUS_LABELS[status]
                                                  : 'Availability not checked'}
                                    </span>
                                </div>
                                <span className="text-muted-foreground text-xs">
                                    {miles !== undefined
                                        ? `${miles.toFixed(1)} mi`
                                        : null}
                                </span>
                            </div>
                        );
                    })}
                    {recipients.searchLoading ? (
                        <p
                            role="status"
                            className="text-muted-foreground text-xs"
                        >
                            Searching caregivers…
                        </p>
                    ) : recipients.searchError ? (
                        <div role="alert" className="text-destructive text-xs">
                            Couldn’t search caregivers.{' '}
                            <Button
                                variant="link"
                                size="sm"
                                onClick={recipients.retrySearch}
                            >
                                Retry search
                            </Button>
                        </div>
                    ) : visibleCaregivers.length === 0 ? (
                        <p className="text-muted-foreground text-xs">
                            {recipients.searching
                                ? recipients.search.trim().length < 2
                                    ? 'Enter at least 2 characters to search all caregivers.'
                                    : 'No caregivers match this search.'
                                : activeShiftIds.length > 0
                                  ? 'No caregivers within this radius match the current filters. Increase the radius or search to add caregivers.'
                                  : 'No caregivers within this radius match the current filters. Increase the radius to see more caregivers.'}
                        </p>
                    ) : null}
                    {recipients.searching ? (
                        recipients.hasMoreSearchResults ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                disabled={recipients.loadingMore}
                                onClick={recipients.loadMoreSearchResults}
                            >
                                {recipients.loadingMore
                                    ? 'Loading caregivers…'
                                    : 'Show more results'}
                            </Button>
                        ) : null
                    ) : visibleCount < recipients.rows.length ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                                setVisibleCount((count) => count + PAGE_SIZE)
                            }
                        >
                            Show more caregivers
                        </Button>
                    ) : null}
                </div>
                {activeShiftIds.length > 0 ? (
                    <Fragment>
                        <p className="text-muted-foreground text-xs">
                            {selectedCaregiverIds.length} selected
                        </p>
                        {!manualAllowed ? (
                            <p className="text-muted-foreground text-xs">
                                Manual outreach is not enabled for this
                                organization.
                            </p>
                        ) : !sendingAllowed ? (
                            <p className="text-muted-foreground text-xs">
                                Outreach sending is not enabled for this
                                organization.
                            </p>
                        ) : null}
                        <Button
                            className="w-full shrink-0"
                            disabled={
                                shifts.isLoading ||
                                !!shifts.error ||
                                !manualAllowed ||
                                !sendingAllowed ||
                                selectedCaregiverIds.length === 0 ||
                                recipients.selectionPending
                            }
                            onClick={() =>
                                setComposer({
                                    shiftIds: activeShiftIds,
                                    caregiverIds: selectedCaregiverIds,
                                })
                            }
                        >
                            Create outreach
                        </Button>
                    </Fragment>
                ) : null}
            </div>
            {profileCaregiverId ? (
                <CaregiverDetailModal
                    isOpen={true}
                    onClose={() => setProfileCaregiverId(null)}
                    organizationId={organizationId}
                    caregiverId={profileCaregiverId}
                />
            ) : null}
            {composer ? (
                <MapManualOutreachModal
                    organizationId={organizationId}
                    onClose={() => setComposer(null)}
                    shiftIds={composer.shiftIds}
                    caregiverIds={composer.caregiverIds}
                />
            ) : null}
        </div>
    );
}
